import { useState } from 'react';

import type { ReactNode } from 'react';

import {
  Box,
  Button,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

import OpenInNewRoundedIcon
  from '@mui/icons-material/OpenInNewRounded';

import ProductCustomerPortfolioDetailModal
  from './ProductCustomerPortfolioDetailModal';

import type {
  MarginCenterComparisonMode,
} from '../types/marginCenter';

import type {
  MarginCenterProductCustomerPortfolioAnalysis,
  MarginCenterProductCustomerPortfolioConcentrationPeriod,
  MarginCenterProductCustomerPortfolioMixItem,
} from '../types/productCustomerPortfolioAnalysis';

import type {
  MarginCenterProductCustomerPortfolioDetailType,
} from '../types/productCustomerPortfolioDetail';


interface ProductCustomerPortfolioAnalysisPanelProps {
  analysis:
    MarginCenterProductCustomerPortfolioAnalysis;

  productId: string;

  startDate: string | null;
  endDate: string | null;

  comparisonMode: MarginCenterComparisonMode;
  comparisonStartDate: string | null;
  comparisonEndDate: string | null;

  currencyCode: string | null;
  hasComparison: boolean;

  onOpenRelationship: (
    customerId: string,
  ) => void;
}


const numberFormatter = new Intl.NumberFormat(
  'es-ES',
  {
    maximumFractionDigits: 2,
  },
);

const integerFormatter = new Intl.NumberFormat(
  'es-ES',
  {
    maximumFractionDigits: 0,
  },
);


function formatInteger(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  return integerFormatter.format(value);
}


function formatPct(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  return `${numberFormatter.format(value)} %`;
}


function formatSignedPoints(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  return `${
    value > 0
      ? '+'
      : ''
  }${numberFormatter.format(value)} pp`;
}


function formatCurrency(
  value: number | null | undefined,
  currencyCode: string | null,
): string {
  if (
    value === null
    || value === undefined
  ) {
    return '—';
  }

  if (!currencyCode) {
    return numberFormatter.format(value);
  }

  try {
    return new Intl.NumberFormat(
      'es-ES',
      {
        style: 'currency',
        currency: currencyCode,
        maximumFractionDigits: 2,
      },
    ).format(value);
  } catch {
    return numberFormatter.format(value);
  }
}


function movementColor(
  value: number | null | undefined,
): string {
  if (
    value === null
    || value === undefined
    || value === 0
  ) {
    return '#94a3b8';
  }

  return value > 0
    ? '#34d399'
    : '#fb7185';
}


function AnalysisCard({
  eyebrow,
  title,
  action,
  children,
}: {
  eyebrow: string;
  title: string;
  action?: ReactNode;
  children: ReactNode;
}) {
  return (
    <Paper
      elevation={0}
      sx={{
        minWidth: 0,
        p: 1.6,
        borderRadius: 2.25,
        border:
          '1px solid rgba(148,163,184,0.14)',
        bgcolor:
          'rgba(2,6,23,0.24)',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          alignItems: 'flex-start',
          justifyContent: 'space-between',
          gap: 1.5,
        }}
      >
        <Box sx={{ minWidth: 0 }}>
          <Typography
            variant="overline"
            sx={{
              color: '#64748b',
              fontSize: '0.68rem',
              fontWeight: 800,
              letterSpacing: 0.9,
              lineHeight: 1.4,
            }}
          >
            {eyebrow}
          </Typography>

          <Typography
            variant="subtitle2"
            sx={{
              mt: 0.15,
              color: '#f8fafc',
              fontWeight: 900,
            }}
          >
            {title}
          </Typography>
        </Box>

        {action && (
          <Box
            sx={{
              flexShrink: 0,
              pt: 0.1,
            }}
          >
            {action}
          </Box>
        )}
      </Box>

      <Box sx={{ mt: 1.1 }}>
        {children}
      </Box>
    </Paper>
  );
}


function ParetoReading({
  period,
  label,
}: {
  period:
    MarginCenterProductCustomerPortfolioConcentrationPeriod;

  label: string;
}) {
  if (period.status !== 'ok') {
    return (
      <Typography
        variant="caption"
        sx={{
          display: 'block',
          color: '#64748b',
          lineHeight: 1.45,
        }}
      >
        {label}: concentración no calculable.
      </Typography>
    );
  }

  return (
    <Typography
      variant="caption"
      sx={{
        display: 'block',
        color: '#94a3b8',
        lineHeight: 1.45,
      }}
    >
      {label}:{' '}
      {formatInteger(
        period.customers_for_80_pct_revenue,
      )}{' '}
      de{' '}
      {formatInteger(
        period.active_customers,
      )}{' '}
      clientes explican el 80 % de los ingresos.
    </Typography>
  );
}


function MixMovementItem({
  item,
  onOpenRelationship,
}: {
  item:
    MarginCenterProductCustomerPortfolioMixItem;

  onOpenRelationship: (
    customerId: string,
  ) => void;
}) {
  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns:
          'minmax(0, 1fr) auto auto',
        gap: 1.2,
        alignItems: 'center',
        py: 0.7,
        borderBottom:
          '1px solid rgba(148,163,184,0.10)',

        '&:last-of-type': {
          borderBottom: 'none',
        },
      }}
    >
      <Typography
        variant="body2"
        noWrap
        title={item.label}
        sx={{
          minWidth: 0,
          color: '#cbd5e1',
          fontWeight: 750,
        }}
      >
        {item.label}
      </Typography>

      <Typography
        variant="caption"
        sx={{
          color: movementColor(
            item.revenue_share_delta_points,
          ),
          fontWeight: 850,
          whiteSpace: 'nowrap',
        }}
      >
        {formatSignedPoints(
          item.revenue_share_delta_points,
        )}
      </Typography>

      <Button
        size="small"
        variant="text"
        endIcon={
          <OpenInNewRoundedIcon
            sx={{
              fontSize: 14,
            }}
          />
        }
        onClick={() =>
          onOpenRelationship(
            item.customer_id,
          )
        }
        sx={{
          minWidth: 0,
          px: 0.5,
          fontSize: '0.7rem',
          textTransform: 'none',
        }}
      >
        Examinar
      </Button>
    </Box>
  );
}


export default function ProductCustomerPortfolioAnalysisPanel({
  analysis,
  productId,
  startDate,
  endDate,
  comparisonMode,
  comparisonStartDate,
  comparisonEndDate,
  currencyCode,
  hasComparison,
  onOpenRelationship,
}: ProductCustomerPortfolioAnalysisPanelProps) {
  const [
    detailModal,
    setDetailModal,
  ] = useState<
    MarginCenterProductCustomerPortfolioDetailType
    | null
  >(null);
  const concentration =
    analysis.concentration.revenue;

  const current =
    concentration.current;

  const comparison =
    concentration.comparison;

  const change =
    concentration.change;

  const core =
    analysis.core_movement;

  const mix =
    analysis.mix_movement;

  const rotation =
    analysis.activity_rotation;

  const topGains =
    mix.largest_share_gains.slice(
      0,
      3,
    );

  const topLosses =
    mix.largest_share_losses.slice(
      0,
      3,
    );

  return (
    <Box sx={{ mt: 2 }}>
      <Stack spacing={0.35}>
        <Typography
          variant="subtitle1"
          sx={{
            color: '#f8fafc',
            fontWeight: 900,
          }}
        >
          Estructura de la cartera de clientes
        </Typography>

        <Typography
          variant="body2"
          sx={{
            color: '#94a3b8',
            lineHeight: 1.5,
          }}
        >
          Cómo se concentra la facturación del producto,
          qué clientes ganan o pierden peso y cómo cambia
          el núcleo de su cartera.
        </Typography>
      </Stack>

      <Box
        sx={{
          mt: 1.5,
          display: 'grid',
          gridTemplateColumns: {
            xs: '1fr',
            xl:
              'repeat(2, minmax(0, 1fr))',
          },
          gap: 1,
        }}
      >
        <AnalysisCard
          eyebrow="Concentración"
          title="Qué parte de la cartera explica los ingresos"
          action={
            current.status === 'ok' ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  setDetailModal('concentration')
                }
                sx={{
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Ver desglose
              </Button>
            ) : null
          }
        >
          {current.status === 'ok' ? (
            <Stack spacing={0.75}>
              <Typography
                variant="h6"
                sx={{
                  color: '#f8fafc',
                  fontWeight: 900,
                }}
              >
                {formatInteger(
                  current
                    .customers_for_80_pct_revenue,
                )}{' '}
                de{' '}
                {formatInteger(
                  current.active_customers,
                )}{' '}
                clientes → 80 % de los ingresos
              </Typography>

              <Typography
                variant="body2"
                sx={{
                  color: '#94a3b8',
                  lineHeight: 1.5,
                }}
              >
                Hace falta el{' '}
                {formatPct(
                  current
                    .customers_for_80_pct_portfolio_share_pct,
                )}{' '}
                de los clientes activos para alcanzar
                el 80 % de la facturación.
              </Typography>

              <Box
                sx={{
                  height: 7,
                  borderRadius: 99,
                  bgcolor:
                    'rgba(148,163,184,0.12)',
                  overflow: 'hidden',
                }}
              >
                <Box
                  sx={{
                    height: '100%',
                    width: `${Math.min(
                      100,
                      Math.max(
                        0,
                        current
                          .top_20_pct_revenue_share_pct
                        ?? 0,
                      ),
                    )}%`,
                    bgcolor: '#38bdf8',
                    borderRadius: 99,
                  }}
                />
              </Box>

              <Typography
                variant="caption"
                sx={{
                  color: '#94a3b8',
                }}
              >
                El 20 % de los clientes genera el{' '}
                {formatPct(
                  current
                    .top_20_pct_revenue_share_pct,
                )}{' '}
                de los ingresos.
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color:
                    current.pareto_80_20_met
                      ? '#38bdf8'
                      : '#64748b',
                  fontWeight: 750,
                }}
              >
                {current.pareto_80_20_met
                  ? 'En este periodo sí se observa una concentración 80/20.'
                  : 'En este periodo no se observa una concentración 80/20.'}
              </Typography>

              {hasComparison
                && comparison && (
                <ParetoReading
                  period={comparison}
                  label="Periodo comparado"
                />
              )}

              {hasComparison
                && change
                && change
                  .customers_for_80_pct_revenue_delta
                  !== null && (
                <Typography
                  variant="caption"
                  sx={{
                    color: movementColor(
                      -change
                        .customers_for_80_pct_revenue_delta,
                    ),
                    fontWeight: 750,
                  }}
                >
                  Frente al comparado hacen falta{' '}
                  {Math.abs(
                    change
                      .customers_for_80_pct_revenue_delta,
                  )}{' '}
                  clientes{' '}
                  {change
                    .customers_for_80_pct_revenue_delta
                    > 0
                    ? 'más'
                    : change
                        .customers_for_80_pct_revenue_delta
                        < 0
                      ? 'menos'
                      : 'de diferencia'}
                  {' '}para alcanzar el 80 %.
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: '#94a3b8',
                lineHeight: 1.5,
              }}
            >
              La concentración no es calculable con
              la estructura neta de ingresos observada
              en esta selección.
            </Typography>
          )}
        </AnalysisCard>

        <AnalysisCard
          eyebrow="Núcleo"
          title="Qué clientes sostienen el núcleo de ingresos"
          action={
            hasComparison
            && core.status === 'ok' ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  setDetailModal('core_movement')
                }
                sx={{
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Ver cambios del núcleo
              </Button>
            ) : null
          }
        >
          {hasComparison
            && core.status === 'ok' ? (
            <Stack spacing={1}>
              <Typography
                variant="h6"
                sx={{
                  color: '#f8fafc',
                  fontWeight: 900,
                }}
              >
                {formatInteger(
                  core.current_core_customers,
                )}{' '}
                clientes en el núcleo actual
              </Typography>

              <Typography
                variant="caption"
                sx={{
                  color: '#94a3b8',
                }}
              >
                {formatInteger(
                  core.comparison_core_customers,
                )}{' '}
                en el periodo comparado.
              </Typography>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm:
                      'repeat(3, minmax(0, 1fr))',
                  },
                  gap: 0.75,
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#e2e8f0',
                      fontWeight: 900,
                    }}
                  >
                    {formatInteger(
                      core.stayed_in_core,
                    )}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color: '#94a3b8',
                    }}
                  >
                    permanecen
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#34d399',
                      fontWeight: 900,
                    }}
                  >
                    {formatInteger(
                      core.joined_current_core,
                    )}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color: '#94a3b8',
                    }}
                  >
                    entran al núcleo
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#fb7185',
                      fontWeight: 900,
                    }}
                  >
                    {formatInteger(
                      core.left_current_core,
                    )}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color: '#94a3b8',
                    }}
                  >
                    salen del núcleo
                  </Typography>
                </Box>
              </Box>

              <Typography
                variant="caption"
                sx={{
                  color: '#64748b',
                  lineHeight: 1.45,
                }}
              >
                Núcleo = conjunto mínimo de clientes
                necesario para alcanzar el 80 % de los
                ingresos observados. No es una
                clasificación comercial.
              </Typography>
            </Stack>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: '#94a3b8',
              }}
            >
              {hasComparison
                ? 'El movimiento del núcleo no es calculable con esta selección.'
                : 'El movimiento del núcleo necesita un periodo de comparación.'}
            </Typography>
          )}
        </AnalysisCard>

        <AnalysisCard
          eyebrow="Mix"
          title="Qué clientes ganan y pierden peso"
          action={
            hasComparison
            && mix.status === 'ok' ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  setDetailModal('mix_movement')
                }
                sx={{
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Ver cambios de mix
              </Button>
            ) : null
          }
        >
          {hasComparison
            && mix.status === 'ok' ? (
            <Stack spacing={1}>
              <Stack
                direction="row"
                spacing={2}
                useFlexGap
                flexWrap="wrap"
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: '#34d399',
                    fontWeight: 850,
                  }}
                >
                  {formatInteger(
                    mix.gaining_share_customers,
                  )}{' '}
                  ganan peso
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#fb7185',
                    fontWeight: 850,
                  }}
                >
                  {formatInteger(
                    mix.losing_share_customers,
                  )}{' '}
                  pierden peso
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: '#94a3b8',
                    fontWeight: 750,
                  }}
                >
                  {formatInteger(
                    mix.continued_customers,
                  )}{' '}
                  comparables
                </Typography>
              </Stack>

              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    lg:
                      'repeat(2, minmax(0, 1fr))',
                  },
                  columnGap: 4,
                  rowGap: 1.5,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#64748b',
                      fontWeight: 750,
                    }}
                  >
                    Mayor ganancia de peso
                  </Typography>

                  <Box sx={{ mt: 0.35 }}>
                    {topGains.length > 0
                      ? topGains.map(
                          (item) => (
                            <MixMovementItem
                              key={
                                item.customer_id
                              }
                              item={item}
                              onOpenRelationship={
                                onOpenRelationship
                              }
                            />
                          ),
                        )
                      : (
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#64748b',
                          }}
                        >
                          Sin ganancias de peso.
                        </Typography>
                      )}
                  </Box>
                </Box>

                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    variant="caption"
                    sx={{
                      color: '#64748b',
                      fontWeight: 750,
                    }}
                  >
                    Mayor pérdida de peso
                  </Typography>

                  <Box sx={{ mt: 0.35 }}>
                    {topLosses.length > 0
                      ? topLosses.map(
                          (item) => (
                            <MixMovementItem
                              key={
                                item.customer_id
                              }
                              item={item}
                              onOpenRelationship={
                                onOpenRelationship
                              }
                            />
                          ),
                        )
                      : (
                        <Typography
                          variant="caption"
                          sx={{
                            color: '#64748b',
                          }}
                        >
                          Sin pérdidas de peso.
                        </Typography>
                      )}
                  </Box>
                </Box>
              </Box>
            </Stack>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: '#94a3b8',
              }}
            >
              {hasComparison
                ? 'El movimiento de mix no es calculable con esta selección.'
                : 'El análisis de mix necesita un periodo de comparación.'}
            </Typography>
          )}
        </AnalysisCard>

        <AnalysisCard
          eyebrow="Rotación"
          title="Qué clientes aparecen y dejan de tener actividad"
          action={
            hasComparison
            && rotation.status === 'ok' ? (
              <Button
                size="small"
                variant="outlined"
                onClick={() =>
                  setDetailModal('activity_rotation')
                }
                sx={{
                  textTransform: 'none',
                  whiteSpace: 'nowrap',
                }}
              >
                Ver rotación
              </Button>
            ) : null
          }
        >
          {hasComparison
            && rotation.status === 'ok' ? (
            <Stack spacing={1}>
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm:
                      'repeat(2, minmax(0, 1fr))',
                  },
                  gap: 1.5,
                }}
              >
                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#34d399',
                      fontWeight: 900,
                    }}
                  >
                    {formatInteger(
                      rotation
                        .entered_current_customers,
                    )}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color: '#94a3b8',
                    }}
                  >
                    solo actuales ·{' '}
                    {formatCurrency(
                      rotation
                        .entered_current_revenue,
                      currencyCode,
                    )}{' '}
                    ·{' '}
                    {formatPct(
                      rotation
                        .entered_current_revenue_share_pct,
                    )}{' '}
                    de ingresos actuales
                  </Typography>
                </Box>

                <Box>
                  <Typography
                    variant="h6"
                    sx={{
                      color: '#fb7185',
                      fontWeight: 900,
                    }}
                  >
                    {formatInteger(
                      rotation
                        .missing_current_customers,
                    )}
                  </Typography>

                  <Typography
                    variant="caption"
                    sx={{
                      color: '#94a3b8',
                    }}
                  >
                    solo comparados ·{' '}
                    {formatCurrency(
                      rotation
                        .missing_comparison_revenue,
                      currencyCode,
                    )}{' '}
                    ·{' '}
                    {formatPct(
                      rotation
                        .missing_comparison_revenue_share_pct,
                    )}{' '}
                    de ingresos comparados
                  </Typography>
                </Box>
              </Box>

              <Typography
                variant="body2"
                sx={{
                  color: movementColor(
                    rotation
                      .net_active_customer_change,
                  ),
                  fontWeight: 800,
                }}
              >
                Saldo de actividad:{' '}
                {rotation
                  .net_active_customer_change
                  !== null
                  && rotation
                    .net_active_customer_change
                    > 0
                  ? '+'
                  : ''}
                {formatInteger(
                  rotation
                    .net_active_customer_change,
                )}{' '}
                clientes.
              </Typography>

              {rotation
                .revenue_replacement_ratio_pct
                !== null && (
                <Typography
                  variant="caption"
                  sx={{
                    color: '#94a3b8',
                    lineHeight: 1.45,
                  }}
                >
                  Los ingresos de clientes solo actuales
                  equivalen al{' '}
                  {formatPct(
                    rotation
                      .revenue_replacement_ratio_pct,
                  )}{' '}
                  de los ingresos que aportaban los
                  clientes solo comparados. Es una
                  comparación de magnitudes; no demuestra
                  sustitución ni pérdida comercial.
                </Typography>
              )}
            </Stack>
          ) : (
            <Typography
              variant="body2"
              sx={{
                color: '#94a3b8',
              }}
            >
              {hasComparison
                ? 'La rotación no es calculable con esta selección.'
                : 'La rotación necesita un periodo de comparación.'}
            </Typography>
          )}
        </AnalysisCard>
      </Box>

      {analysis.limitations.length > 0 && (
        <Typography
          variant="caption"
          sx={{
            display: 'block',
            mt: 1,
            color: '#64748b',
          }}
        >
          Parte del análisis de concentración no es
          calculable con la estructura neta de ingresos
          observada en esta selección.
        </Typography>
      )}

      <ProductCustomerPortfolioDetailModal
        open={detailModal !== null}
        detail={
          detailModal
          ?? 'concentration'
        }
        productId={productId}
        startDate={startDate}
        endDate={endDate}
        comparisonMode={comparisonMode}
        comparisonStartDate={
          comparisonStartDate
        }
        comparisonEndDate={
          comparisonEndDate
        }
        currencyCode={currencyCode}
        onOpenRelationship={
          onOpenRelationship
        }
        onClose={() =>
          setDetailModal(null)
        }
      />
    </Box>
  );
}
